import { Injectable } from "@angular/core";
import { Plugins } from "@capacitor/core";
import { ExtractDocument } from "../models/extract-document";
import { Session } from "../models/session/session.model";
import { BuilderPatternService } from "./builder-pattern.service";
import { DocumentSelected } from "../models/document-selected";
import { CryptoService } from "./crypto.service";
import { MemoryStorageService } from "./memory-storage.service";

const { Storage } = Plugins;

@Injectable({
    providedIn: "root",
})
export class StorageService {
    constructor(
        private builderPatternSrv: BuilderPatternService,
        private cryptoService: CryptoService,
        private memoryStorage: MemoryStorageService
    ) {
    }

    private async setEncrypted(key: string, value: any): Promise<void> {
        const encryptedValue = this.cryptoService.encrypt(value);
        return await Storage.set({
            key,
            value: encryptedValue
        });
    }

    private async getDecrypted<T = any>(key: string): Promise<T | null> {
        const result = await Storage.get({ key });
        if (!result.value) {
            return null;
        }
        return this.cryptoService.decrypt<T>(result.value);
    }

    public setLocalStorageEncrypted(key: string, value: any): void {
        const encryptedValue = this.cryptoService.encrypt(value);
        localStorage.setItem(key, encryptedValue);
    }

    public getLocalStorageDecrypted<T = any>(key: string): T | null {
        const encryptedValue = localStorage.getItem(key);
        if (!encryptedValue) {
            return null;
        }
        return this.cryptoService.decrypt<T>(encryptedValue);
    }

    public removeLocalStorage(key: string): void {
        localStorage.removeItem(key);
    }

    public setSessionStorageEncrypted(key: string, value: any): void {
        const encryptedValue = this.cryptoService.encrypt(value);
        sessionStorage.setItem(key, encryptedValue);
    }

    public getSessionStorageDecrypted<T = any>(key: string): T | null {
        const encryptedValue = sessionStorage.getItem(key);
        if (!encryptedValue) {
            return null;
        }
        return this.cryptoService.decrypt<T>(encryptedValue);
    }

    public removeSessionStorage(key: string): void {
        sessionStorage.removeItem(key);
    }

    public async setIneFrontImage(image: string): Promise<void> {
        // Store images in memory instead of localStorage to avoid size limits
        this.memoryStorage.set("ineFront", image);
    }

    public async getIneFrontImage(): Promise<string> {
        return this.memoryStorage.get<string>("ineFront");
    }

    public async removeIneFront(): Promise<void> {
        this.memoryStorage.remove("ineFront");
    }

    public async setEventId(id: string): Promise<void> {
        return await this.setEncrypted("eventId", id);
    }

    public async getEventId(): Promise<string> {
        return await this.getDecrypted<string>("eventId");
    }

    public async removeEventId(): Promise<void> {
        return await Storage.remove({
            key: "eventId",
        });
    }

    public async setDocument(doc: ExtractDocument): Promise<void> {
        return await this.setEncrypted("doc", doc);
    }

    public async getDocument(): Promise<ExtractDocument> {
        return await this.getDecrypted<ExtractDocument>("doc");
    }

    public async removeDocument(): Promise<void> {

        return await Storage.remove({
            key: "doc",
        });
    }

    public async setDocumentId(id: string): Promise<void> {
        return await this.setEncrypted("documentId", id);
    }

    public async getDocumentId(): Promise<string> {
        return await this.getDecrypted<string>("documentId");
    }

    public async removeIneBackCaptured(): Promise<void> {
        return await Storage.remove({
            key: "captureBack",
        });
    }

    public async setBestFrame(value: string): Promise<void> {
        // Store video frames in memory instead of localStorage to avoid size limits
        this.memoryStorage.set("bestFrame", value);
    }

    public async getBestFrame(): Promise<any> {
        return this.memoryStorage.get<any>("bestFrame");
    }

    public async removeBestFrame(): Promise<void> {
        this.memoryStorage.remove("bestFrame");
    }

    public async setIdentityResponse(value: any): Promise<void> {
        return await this.setEncrypted("identityResponse", value);
    }

    public async getIdentityResponse(): Promise<any> {
        return await this.getDecrypted<any>("identityResponse");
    }

    public async removeIdentityResponse(): Promise<void> {
        return await Storage.remove({
            key: "identityResponse",
        });
    }

    public async setSession(session: any): Promise<void> {
        return await this.setEncrypted("session", session);
    }

    public async getSession(): Promise<Session> {
        const session = await this.getDecrypted<any>("session");
        
        if (!session) {
            return null;
        }

        return Session.from(session);
    }

    public async removeSession(): Promise<void> {
        return await Storage.remove({
            key: "session",
        });
    }

    public async setStep(step: string): Promise<void> {
        return await this.setEncrypted("step", step);
    }

    public async getStep(): Promise<string> {
        return await this.getDecrypted<string>("step");
    }

    public async removeStep(): Promise<void> {
        return await Storage.remove({
            key: "flowStep",
        });
    }

    public async setFlowStep(step: string): Promise<void> {
        return await this.setEncrypted("flowStep", step);
    }

    public async getFlowStep(): Promise<string> {
        return await this.getDecrypted<string>("flowStep");
    }

    public async removeFlowStep(): Promise<void> {
        return await Storage.remove({
            key: "flowStep",
        });
    }

    public async removeStorage(): Promise<void> {
        await this.removeDocument();
        await this.removeEventId();
        await this.removeIneFront();
        await this.removeIneBackCaptured();
        await this.removeBestFrame();
        await this.removeStep();
        await this.removeIdentityResponse();
        // Clear memory storage for images and videos
        this.memoryStorage.clear();
    }

    public clear(): Promise<void> {
        // Clear both persistent storage and memory storage
        this.memoryStorage.clear();
        return Storage.clear();
    }
}
